// Sentry reports made by the API transport (ts/client.ts).
//
// The client aborts a request after 30 seconds; the view shows that failure,
// while the server may still be working. The request body — a ledger's filters,
// the footer's columns, the page and the mint flag — therefore exists only here,
// which is exactly what a slow-query investigation needs: the operator, the
// endpoint(s) and the narrowing that was sent.
//
// Reporting at the transport layer also keeps the report in step with what was
// actually asked. A ledger load launches its reads together (the page, the count
// strip, the footer, the map sets: ts/store/map-review.ts loadLedger), so a slow
// filter times all of them out at once. Those rejections are gathered for a
// moment and sent as one event, so one slow filter is one report carrying the
// whole round rather than one issue per endpoint.
//
// The user is attached to the scope by identifySentrySession, so every later
// event carries the operator and the district the filters were read under.

import * as Sentry from "@sentry/vue";
import type { AxiosError } from "axios";
import type { Session } from "@/type/api";

/** The longest request body a report carries. A timed-out body is normally the
 * ledger's filter list; the cap keeps a large upload from flooding the event
 * when one is what took too long. */
const MAX_BODY_CHARS = 16 * 1024;

/** How long a round's timeouts are gathered before one event is sent. The
 * ledger's reads are launched together and abort at the same instant, so this
 * only has to span the gap between their rejections. */
const GATHER_MS = 500;

/** The most requests one report carries, so a pathological loop cannot grow the
 * pending set without bound. */
const MAX_REQUESTS = 32;

interface TimedOutRequest {
	method: string;
	endpoint: string;
	page: string | null;
	timeout_ms: number | null;
	body: unknown;
}

let pending: TimedOutRequest[] = [];
let gatherTimer: number | null = null;

/**
 * Identify the signed-in operator on the Sentry scope, or clear it on sign-out.
 * The user is the report's subject; the district and role are contexts because
 * they decide which organization's data a filter reads, and impersonation is a
 * tag because it changes who is responsible for what the report shows.
 */
export function identifySentrySession(session: Session | null): void {
	if (session === null) {
		Sentry.setUser(null);
		Sentry.setContext("organization", null);
		Sentry.setTag("role", undefined);
		Sentry.setTag("impersonating", undefined);
		return;
	}
	Sentry.setUser({
		id: String(session.self.id),
		username: session.self.username,
	});
	Sentry.setContext("organization", {
		id: session.organization.id,
		name: session.organization.name,
	});
	Sentry.setTag("role", session.self.role);
	Sentry.setTag("impersonating", session.impersonating);
}

/** True when axios rejected the request because its own 30-second timeout
 * elapsed. A server error is not a timeout, however slow the response was. */
export function isRequestTimeout(error: unknown): error is AxiosError {
	if (typeof error !== "object" || error === null) return false;
	const { code } = error as { code?: unknown };
	return code === "ECONNABORTED" || code === "ETIMEDOUT";
}

/** Record one timed-out request. Reports that reject in the same moment (a
 * ledger round's reads) are flushed together. */
export function reportRequestTimeout(error: AxiosError): void {
	if (pending.length >= MAX_REQUESTS) return;
	const config = error.config;
	pending.push({
		method: (config?.method ?? "GET").toUpperCase(),
		endpoint: config?.url ?? "(unknown endpoint)",
		page:
			typeof window === "undefined"
				? null
				: window.location.pathname + window.location.search,
		timeout_ms: typeof config?.timeout === "number" ? config.timeout : null,
		body: bodyForReport(config?.data),
	});
	gatherTimer ??= setTimeout(flushTimeouts, GATHER_MS);
}

function flushTimeouts(): void {
	gatherTimer = null;
	const requests = pending;
	pending = [];
	if (requests.length === 0) return;
	// The endpoint set is the report's identity: a slow filter is the
	// combination of reads it made, and an endpoint that keeps timing out is
	// one issue that shows every operator and filter that hit it.
	const endpoints = [...new Set(requests.map((request) => request.endpoint))];
	const timeoutMS = requests[0].timeout_ms;
	Sentry.captureEvent({
		level: "error",
		message: "API request timed out",
		fingerprint: ["api-request-timeout", endpoints.sort().join(",")],
		tags: {
			"api.timeout_ms": timeoutMS ?? undefined,
			"api.endpoints": endpoints.join(","),
		},
		contexts: {
			api_timeout: {
				requests: requests.length,
				endpoints,
				page: requests[0].page,
				timeout_ms: timeoutMS,
			},
		},
		extra: { requests },
	});
}

/** The body as the report should carry it: parsed JSON when the body is JSON,
 * a bounded preview otherwise. Axios normally hands the serialized body over,
 * but a caller may pass an object directly, and a non-JSON body (a FormData
 * upload) is described rather than stringified. */
function bodyForReport(data: unknown): unknown {
	if (data === undefined || data === null) return null;
	let text: string | null = null;
	if (typeof data === "string") {
		text = data;
	} else {
		try {
			text = JSON.stringify(data) ?? null;
		} catch {
			text = null;
		}
	}
	if (text === null)
		return `[${Object.prototype.toString.call(data).slice(8, -1)}]`;
	if (text.length > MAX_BODY_CHARS) {
		return {
			truncated: true,
			characters: text.length,
			preview: text.slice(0, MAX_BODY_CHARS),
		};
	}
	try {
		return JSON.parse(text) as unknown;
	} catch {
		return text;
	}
}
